import { useEffect, type RefObject } from "react";

const EDGE_PX = 32;
const MIN_SWIPE_PX = 70;

function isNarrowMobile(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;
}

/**
 * Retour conversation sur mobile :
 * - bord gauche → glisser vers la droite (classique / gaucher)
 * - bord droit → glisser vers la gauche (droitier)
 */
export function useChatEdgeSwipeBack({
  enabled,
  targetRef,
  onBack,
}: {
  enabled: boolean;
  targetRef: RefObject<HTMLElement | null>;
  onBack: () => void;
}) {
  useEffect(() => {
    if (!enabled || !isNarrowMobile()) return;
    const el = targetRef.current;
    if (!el) return;

    let startX = 0;
    let startY = 0;
    let edge: "left" | "right" | null = null;
    let armed = false;
    let fired = false;

    const reset = () => {
      edge = null;
      armed = false;
      fired = false;
    };

    const onTouchStart = (e: TouchEvent) => {
      if (!isNarrowMobile()) return;
      if (e.touches.length !== 1) {
        reset();
        return;
      }
      const t = e.touches[0]!;
      const w = window.innerWidth;
      startX = t.clientX;
      startY = t.clientY;
      fired = false;
      if (startX <= EDGE_PX) {
        edge = "left";
        armed = true;
      } else if (startX >= w - EDGE_PX) {
        edge = "right";
        armed = true;
      } else {
        edge = null;
        armed = false;
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!armed || !edge || fired || e.touches.length !== 1) return;
      const t = e.touches[0]!;
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;

      // Scroll vertical prioritaire
      if (Math.abs(dy) > 28 && Math.abs(dy) > Math.abs(dx) * 1.1) {
        armed = false;
        return;
      }

      const horizontalEnough = Math.abs(dx) >= MIN_SWIPE_PX && Math.abs(dx) > Math.abs(dy) * 1.15;
      if (!horizontalEnough) return;

      if (edge === "left" && dx > 0) {
        fired = true;
        armed = false;
        onBack();
      } else if (edge === "right" && dx < 0) {
        fired = true;
        armed = false;
        onBack();
      }
    };

    const onTouchEnd = () => reset();

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: true });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("touchcancel", onTouchEnd, { passive: true });

    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [enabled, targetRef, onBack]);
}
